import React, { useState } from "react";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useEmpresa } from "../context/EmpresaContext";
import logoCapta from "../assets/logo-capta.png";

export default function Login() {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [showSenha, setShowSenha] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();
  const { salvarEmpresas, trocarEmpresa } = useEmpresa();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCarregando(true);
    setErro(null);

    let progressoAtual = 0;
    const animarProgresso = setInterval(() => {
      progressoAtual += 10;
      if (progressoAtual <= 90) setProgresso(progressoAtual);
      else clearInterval(animarProgresso);
    }, 150);

    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

      const res = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: String(email || "")
            .trim()
            .toLowerCase(),
          senha: String(senha || ""),
        }),
      });

      if (!res.ok) {
        setErro("Usuário ou senha inválidos.");
        setCarregando(false);
        clearInterval(animarProgresso);
        return;
      }

      const data = await res.json();
      if (data?.token) localStorage.setItem("token", data.token);

      const usuario = data.usuario || {};
      const usuarioLogado = {
        id: usuario.id,
        nome: usuario.nome,
        email: usuario.email,
        empresa_id: usuario.empresa_id,
        foto: usuario.foto || `https://i.pravatar.cc/100?u=${usuario.email}`,
        acessos: usuario.acessos || 1,
        permissoes: usuario.permissoes || {},
        cargo: usuario.cargo,
      };

      localStorage.setItem("empresa_id", String(usuario.empresa_id || ""));
      localStorage.setItem("usuario", JSON.stringify(usuarioLogado));
      localStorage.setItem("usuarioLogado", JSON.stringify(usuarioLogado));

      const token = data.token || localStorage.getItem("token");

      const empresasRes = await fetch(`${API_BASE_URL}/empresas`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!empresasRes.ok) {
        throw new Error("Não foi possível carregar as empresas.");
      }

      const empresas = await empresasRes.json();
      salvarEmpresas(Array.isArray(empresas) ? empresas : []);

      const emp =
        Array.isArray(empresas) && empresas.length === 1
          ? empresas[0]
          : Array.isArray(empresas)
            ? empresas.find((e) => e?.id === usuarioLogado.empresa_id)
            : null;

      if (emp) trocarEmpresa(emp);

      setProgresso(100);
      clearInterval(animarProgresso);

      setTimeout(() => {
        login({ token: data.token, usuario: usuarioLogado });
        navigate("/dashboard");
      }, 900);
    } catch (err) {
      setErro("Erro ao conectar com o servidor.");
      setCarregando(false);
    }
  };

  if (carregando) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-white">
        <img
          src={logoCapta}
          alt="Carregando CaptaCRM"
          className="w-24 mb-4 animate-pulse drop-shadow-2xl"
        />
        <div className="w-60 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-700 transition-all duration-300"
            style={{ width: `${progresso}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-blue-900 to-blue-600 flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl shadow-2xl px-6 pb-6 pt-2 max-w-md w-full">
        <div className="flex justify-center">
          <img
            src={logoCapta}
            alt="CaptaCRM"
            className="w-40 drop-shadow-2xl"
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {erro && (
            <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-2 rounded-xl text-sm font-medium">
              {erro}
            </div>
          )}

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-blue-900 mb-1 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-800" /> Email
            </label>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="p-3 border border-blue-300 rounded-xl text-blue-900 outline-blue-700 focus:ring-2 focus:ring-blue-200"
              required
            />
          </div>

          <div className="flex flex-col relative">
            <label className="text-sm font-semibold text-blue-900 mb-1 flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-800" /> Senha
            </label>
            <input
              type={showSenha ? "text" : "password"}
              autoComplete="current-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              className="p-3 border border-blue-300 rounded-xl pr-10 text-blue-900 outline-blue-700 focus:ring-2 focus:ring-blue-200"
              required
            />
            <button
              type="button"
              onClick={() => setShowSenha((v) => !v)}
              className="absolute right-3 top-9 text-blue-800 hover:text-blue-950"
              aria-label={showSenha ? "Ocultar senha" : "Mostrar senha"}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              {showSenha ? <EyeOff size={22} /> : <Eye size={22} />}
            </button>
          </div>

          {/* 🔑 Esqueci minha senha */}
          <div className="flex justify-end -mt-1">
            <Link
              to="/forgot-senha"
              className="text-sm font-semibold text-blue-900 hover:text-blue-950 underline underline-offset-4"
            >
              Esqueci minha senha
            </Link>
          </div>

          <button
            type="submit"
            className="w-full py-3 rounded-xl bg-blue-800 text-white font-semibold shadow-md hover:bg-blue-900 hover:shadow-lg transition-all duration-200"
          >
            Entrar
          </button>
        </form>

        <p className="text-center text-sm text-gray-800 mt-4 font-medium">
          © {new Date().getFullYear()} CaptaCRM. AWC Consultoria Sistemas Ltda.
        </p>
      </div>
    </div>
  );
}
